import React, { useState, useEffect, useCallback, useMemo } from "react";
import IndividualKycWrapper from "./IndividualKycWrapper";
import HorizontalStepper from "./HorizontalStepper";
import PaymentValidationApplicationForm from "./components/PaymentValidationApplicationForm";
import KycModeSelection from "./components/KycModeSelection";
import OfflineKycStepContent from "./components/OfflineKycStepContent";
import { useBidderKycData } from "./hooks/useBidderKycData";
import { useKycModeSelection } from "./hooks/useKycModeSelection";
import {
  STEPS_TITLES,
  findFirstPendingStep,
  determineKycType,
  getStepIndex,
} from "./utils/kycStepUtils";
import { useRequiresApproverDetails } from "./utils/useRequiresApproverDetails";
import KYCBackButtonWithListingId from "./components/KYCBackButtonWithListingId";

interface IndividualKycModeWrapperProps {
  bidderUuid: any;
}

export default function IndividualKycModeWrapper({
  bidderUuid,
}: IndividualKycModeWrapperProps) {
  const {
    bidder,
    loading,
    kycMode,
    offlinePaymentValidation,
    offlinePaymentValidationState,
    initialStep,
    init,
  } = useBidderKycData(bidderUuid);

  const [activeStep, setActiveStep] = useState(-1);

  // Function to recalculate and set the correct step based on current bidder data
  const recalculateAndSetStep = useCallback(() => {
    if (!bidder || kycMode !== "offline") return;

    const kycType = determineKycType(bidder);
    const accessType = bidder?.access_type;
    const step = findFirstPendingStep(
      bidder?.meta?.steps_status,
      kycType,
      kycMode,
      accessType,
    );
    const calculatedStep = getStepIndex(step);

    // Only update if the calculated step is different from current activeStep
    // This prevents unnecessary updates and conflicts
    if (calculatedStep !== activeStep && calculatedStep !== -1) {
      setActiveStep(calculatedStep);
    }
  }, [bidder, kycMode, activeStep]);

  // Sync activeStep with initialStep when it changes (on initial load)
  useEffect(() => {
    if (initialStep !== -1 && activeStep === -1) {
      setActiveStep(initialStep);
    }
  }, [initialStep, activeStep]);

  // Recalculate step when bidder data changes (after updates)
  // This ensures that when a step is completed/rejected, we navigate to the correct next step
  useEffect(() => {
    if (bidder && kycMode === "offline" && !loading && activeStep !== -1) {
      // Use a small delay to ensure state updates are complete
      const timeoutId = setTimeout(() => {
        recalculateAndSetStep();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidder?.meta?.steps_status, kycMode, loading]);

  const { selectingMode, handleKycModeSelection } = useKycModeSelection(
    bidderUuid,
    () => init(bidderUuid),
  );

  const offlineEnabled =
    process.env.NEXT_PUBLIC_ENABLE_OFFLINE_INDIVIDUALS_KYC === "true";

  const requiresApproverDetails = useRequiresApproverDetails(bidder, kycMode);
  const offlineStepsTitles = useMemo(() => {
    if (!requiresApproverDetails) {
      return STEPS_TITLES.filter((step) => step.value !== "approver_details");
    }
    return STEPS_TITLES;
  }, [requiresApproverDetails]);

  if (
    offlinePaymentValidation === true &&
    offlinePaymentValidationState === "pending"
  ) {
    return (
      <PaymentValidationApplicationForm
        bidder={bidder}
        bidderUuid={bidderUuid}
        init={init}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
      />
    );
  }

  return (
    <div>
      {loading ? (
        <div className="flex flex-col flex-grow h-full">
          <div className="flex justify-center items-center">
            <div className="w-12 h-12 border-8 border-t-6 border-brand-orange rounded-full animate-spin bg-gradient-to-r from-brand-orange to-blue-100 shadow-lg"></div>
          </div>
        </div>
      ) : (
        <div>
          <KYCBackButtonWithListingId
            bidderAuctionId={bidder?.meta?.event_id}
          />
          {kycMode === "offline" && (
            <div>
              <HorizontalStepper
                totalSteps={offlineStepsTitles.length}
                activeStep={activeStep}
                setActiveStep={setActiveStep}
                stepsTitles={offlineStepsTitles}
              />
              <OfflineKycStepContent
                activeStep={activeStep}
                bidderUuid={bidderUuid}
                bidder={bidder}
                setActiveStep={(step: number) => {
                  setActiveStep(step);
                }}
                init={async (uuid?: any) => {
                  await init(uuid || bidderUuid);
                  await recalculateAndSetStep();
                }}
                offlinePaymentValidation={offlinePaymentValidation}
                stepsTitles={offlineStepsTitles}
                requiresApproverDetails={requiresApproverDetails}
              />
            </div>
          )}
          {kycMode === "online" && (
            <IndividualKycWrapper bidderUuid={bidderUuid} />
          )}
          {kycMode !== "online" && kycMode !== "offline" && (
            <KycModeSelection
              onSelectMode={handleKycModeSelection}
              selectingMode={selectingMode}
            />
          )}
        </div>
      )}
    </div>
  );
}
